import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const ReviewsSection = ({ product }) => {
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [sortBy, setSortBy] = useState('newest');

  const reviews = [
    {
      id: 1,
      user: {
        name: "Sarah Johnson",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b9e0d1c4?w=100&h=100&fit=crop&crop=face"
      },
      rating: 5,
      title: "Excellent quality and freshness!",
      comment: `I've been ordering these organic apples for months now and they never disappoint. Always fresh, crisp, and perfectly sweet. The packaging is also excellent - no bruised fruits. Highly recommend for anyone looking for premium quality produce.`,
      date: "2024-01-15",
      helpful: 12,
      verified: true
    },
    {
      id: 2,
      user: {
        name: "Mike Chen",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
      },
      rating: 4,
      title: "Good value for money",
      comment: `Great taste and quality. The apples arrived fresh and well-packaged. Only minor complaint is that some were slightly smaller than expected, but overall very satisfied with the purchase.`,
      date: "2024-01-10",
      helpful: 8,
      verified: true
    },
    {
      id: 3,
      user: {
        name: "Emily Rodriguez",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
      },
      rating: 5,
      title: "Perfect for my family",
      comment: `My kids love these apples! They're sweet, crunchy, and perfect for school lunches. The organic certification gives me peace of mind about what my family is eating.`,
      date: "2024-01-08",
      helpful: 15,
      verified: true
    }
  ];

  const ratingDistribution = [
    { stars: 5, count: 45, percentage: 75 },
    { stars: 4, count: 12, percentage: 20 },
    { stars: 3, count: 2, percentage: 3 },
    { stars: 2, count: 1, percentage: 2 },
    { stars: 1, count: 0, percentage: 0 }
  ];

  const renderStars = (rating, size = 16) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon
          key={i}
          name="Star"
          size={size}
          className={i <= rating ? "text-warning fill-current" : "text-border"}
        />
      );
    }
    return stars;
  };

  const handleWriteReview = () => {
    setShowWriteReview(true);
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();
    // Handle review submission
    console.log('Review submitted:', newReview);
    setShowWriteReview(false);
    setNewReview({ rating: 5, title: '', comment: '' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-surface rounded-card shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <h3 className="font-heading font-heading-semibold text-xl text-text-primary">
            Customer Reviews ({product.reviewCount})
          </h3>
          <Button
            variant="primary"
            onClick={handleWriteReview}
            iconName="Edit"
            iconPosition="left"
          >
            Write a Review
          </Button>
        </div>
      </div>

      {/* Rating Overview */}
      <div className="p-6 border-b border-border">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Overall Rating */}
          <div className="text-center md:text-left">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
              <div className="text-4xl font-heading font-heading-bold text-primary">
                {product.rating}
              </div>
              <div>
                <div className="flex justify-center md:justify-start space-x-1 mb-1">
                  {renderStars(Math.round(product.rating), 20)}
                </div>
                <p className="text-text-secondary font-body">
                  Based on {product.reviewCount} reviews
                </p>
              </div>
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="space-y-2">
            {ratingDistribution.map((item) => (
              <div key={item.stars} className="flex items-center space-x-3">
                <span className="font-data text-sm w-6">{item.stars}</span>
                <Icon name="Star" size={14} className="text-warning fill-current" />
                <div className="flex-1 bg-background rounded-full h-2">
                  <div
                    className="bg-warning h-2 rounded-full transition-smooth"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
                <span className="font-data text-sm text-text-secondary w-8">
                  {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Write Review Form */}
      {showWriteReview && (
        <div className="p-6 border-b border-border bg-background">
          <form onSubmit={handleSubmitReview} className="space-y-4">
            <h4 className="font-heading font-heading-semibold text-lg text-text-primary">
              Write Your Review
            </h4>
            
            {/* Rating Selection */}
            <div>
              <label className="block font-body-semibold text-text-primary mb-2">
                Your Rating
              </label>
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setNewReview({ ...newReview, rating: star })}
                    className="p-1"
                  >
                    <Icon
                      name="Star"
                      size={24}
                      className={star <= newReview.rating ? "text-warning fill-current" : "text-border"}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Review Title */}
            <div>
              <label className="block font-body-semibold text-text-primary mb-2">
                Review Title
              </label>
              <Input
                type="text"
                placeholder="Summarize your experience"
                value={newReview.title}
                onChange={(e) => setNewReview({ ...newReview, title: e.target.value })}
                required
              />
            </div>

            {/* Review Comment */}
            <div>
              <label className="block font-body-semibold text-text-primary mb-2">
                Your Review
              </label>
              <textarea
                placeholder="Share your thoughts about this product..."
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-input focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                required
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-3">
              <Button type="submit" variant="primary">
                Submit Review
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowWriteReview(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Reviews List */}
      <div className="p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-2 sm:space-y-0">
          <h4 className="font-heading font-heading-semibold text-lg text-text-primary">
            Reviews
          </h4>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-border rounded-input focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>

        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border-b border-border pb-6 last:border-b-0 last:pb-0">
              <div className="flex items-start space-x-4">
                <Image
                  src={review.user.avatar}
                  alt={review.user.name}
                  className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <h5 className="font-body-semibold text-text-primary">
                        {review.user.name}
                      </h5>
                      {review.verified && (
                        <div className="flex items-center space-x-1 text-success">
                          <Icon name="CheckCircle" size={14} />
                          <span className="text-xs font-data">Verified Purchase</span>
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-text-secondary font-data">
                      {formatDate(review.date)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="flex space-x-1">
                      {renderStars(review.rating)}
                    </div>
                    <span className="font-data text-sm text-text-secondary">
                      {review.rating}/5
                    </span>
                  </div>
                  
                  <h6 className="font-body-semibold text-text-primary mb-2">
                    {review.title}
                  </h6>
                  
                  <p className="text-text-primary font-body leading-relaxed mb-3">
                    {review.comment}
                  </p>
                  
                  <div className="flex items-center space-x-4">
                    <button className="flex items-center space-x-1 text-text-secondary hover:text-primary transition-smooth">
                      <Icon name="ThumbsUp" size={14} />
                      <span className="text-sm font-data">Helpful ({review.helpful})</span>
                    </button>
                    <button className="flex items-center space-x-1 text-text-secondary hover:text-primary transition-smooth">
                      <Icon name="Flag" size={14} />
                      <span className="text-sm font-data">Report</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Reviews */}
        <div className="text-center mt-6">
          <Button variant="outline">
            Load More Reviews
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewsSection;